import styled, { css, DefaultTheme } from 'styled-components';

import { darken } from '@savgroup-front-common/primitive/helpers/cssColor';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { rem } from '@savgroup-front-common/primitive/helpers/units';
import { FLEX_DIRECTION } from '@savgroup-front-common/types';

import { getTextColorBasedOnProps } from '../../../theme/helpers';
import { fontStyle } from '../../../theme/helpers/fontStyle';

const dot = 30;
const width = 60;
const border = 3;

const smallDot = 17;
const smallWidth = 37;

interface $StyledSwitchProps {
  theme: DefaultTheme;
  disabled?: boolean;
  danger?: boolean;
  alignLabel?: FLEX_DIRECTION;
  $small?: boolean;
}

const colorBasedOnProps = ({ theme, disabled, danger }: $StyledSwitchProps) => {
  if (disabled) {
    return theme.colors.disabled;
  }
  if (danger) {
    return theme.colors.danger;
  }

  return theme.colors.default;
};

const sizeBasedOnProps = (isSmall: boolean) => {
  return isSmall
    ? { width: rem(smallWidth), dot: rem(smallDot) }
    : { width: rem(width), dot: rem(dot) };
};

export const $StyledSwitch = styled.span<$StyledSwitchProps>`
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $small }) => sizeBasedOnProps(!!$small).width};
  height: ${({ $small }) => sizeBasedOnProps(!!$small).dot};
  border-radius: ${({ $small }) => sizeBasedOnProps(!!$small).width};
  background-color: ${colorBasedOnProps};
  transition-duration: 0.2s;

  transform-origin: center center;

  &::after {
    content: '';
    display: block;
    position: relative;
    border-radius: 50%;
    transform: translateY(${rem(border)}) translateX(${rem(border)}) scale(1);
    width: ${({ $small }) =>
      $small ? rem(smallDot - border * 2) : rem(dot - border * 2)};
    height: ${({ $small }) =>
      $small ? rem(smallDot - border * 2) : rem(dot - border * 2)};
    background-color: #ffffff;
    box-shadow: 0 ${spacing('xs')} ${spacing('xs')} 0 rgba(10, 31, 68, 0.16);
    transition-duration: 0.2s;
  }
`;

export const $SwitchInput = styled.input.attrs({
  type: 'checkbox',
  role: 'checkbox',
})<{ $small?: boolean }>`
  position: relative;
  z-index: 1;
  width: ${({ $small }) => sizeBasedOnProps(!!$small).width};
  height: ${({ $small }) => sizeBasedOnProps(!!$small).dot};
  opacity: 0;

  &:not(:disabled) {
    cursor: pointer;
    + ${$StyledSwitch} {
      background-color: ${({ theme }) => theme.colors.default};
    }
  }
  &:disabled {
    cursor: not-allowed;
    + ${$StyledSwitch} {
      background-color: ${({ theme }) => theme.colors.disabled};
    }
  }

  &:focus,
  &:hover {
    &:not(:disabled) {
      &:checked + ${$StyledSwitch} {
        background-color: ${({ theme }) => darken(0.15, theme.colors.primary)};
      }
      &:not(:checked) + ${$StyledSwitch} {
        background-color: ${({ theme }) => darken(0.15, theme.colors.default)};
      }
    }
  }

  &:active {
    + ${$StyledSwitch}::after {
      transform: translateY(${rem(border)}) translateX(${rem(border)})
        scale(0.9);
    }
    &:checked + ${$StyledSwitch}::after {
      transform: translateY(${rem(border)})
        translateX(
          ${({ $small }) =>
            $small
              ? rem(smallWidth + border - smallDot)
              : rem(width + border - dot)}
        )
        scale(0.9);
    }
  }

  &:checked {
    + ${$StyledSwitch} {
      background-color: ${({ theme }) => theme.colors.primary};

      &::after {
        transform: translateY(${rem(border)})
          translateX(
            ${({ $small }) =>
              $small
                ? rem(smallWidth + border - smallDot)
                : rem(width + border - dot)}
          )
          scale(1);
      }
    }
  }
`;

const SwitchContainerMixin = css<$StyledSwitchProps>`
  display: flex;
  gap: ${spacing('sm')};
  position: relative;
  flex-direction: ${({ alignLabel }) => alignLabel};
  color: ${getTextColorBasedOnProps};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  span {
    line-height: ${rem(dot)};
  }
  width: fit-content;

  ${({ $small }) =>
    $small
      ? css`
          ${fontStyle('xs')};
          font-weight: ${fontWeight('normal')};
          align-items: center;
        `
      : undefined};
`;

export const $Label = styled.label<$StyledSwitchProps>`
  ${SwitchContainerMixin}
`;
export const $LabelContainer = styled.div<{ alignLabel?: FLEX_DIRECTION }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: ${({ alignLabel }) => {
    return alignLabel === FLEX_DIRECTION.COLUMN
      ? `${spacing('sm')} 0`
      : `0 ${spacing('sm')} 0 0`;
  }};
  & span {
    line-height: 1.2 !important;
  }
`;
export const $SwitchContainer = styled.div<$StyledSwitchProps>`
  ${SwitchContainerMixin}
`;
